<template>
  <div class="login-layout" :class="{ 'login-layout--setup': isSetupFlow }">
    <!-- Left: branding -->
    <div class="login-left d-none d-lg-flex">
      <div class="login-brand">
        <div class="login-logo">
          <i class="fas fa-vial"></i>
        </div>
        <h1 class="login-app-name">TestOps Hub</h1>
        <p class="login-tagline">
          Centralize your QA workflow — test plans,<br />
          test runs, and Jira in one place.
        </p>
        <div class="login-features">
          <div class="login-feature-item">
            <i class="fas fa-check-circle"></i>
            <span>Test Plan Management</span>
          </div>
          <div class="login-feature-item">
            <i class="fas fa-check-circle"></i>
            <span>Sprint & Jira Integration</span>
          </div>
          <div class="login-feature-item">
            <i class="fas fa-check-circle"></i>
            <span>QA Progress Dashboard</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right: form -->
    <div class="login-right">
      <div class="login-card">
        <!-- Mobile logo -->
        <div class="login-mobile-logo d-flex d-lg-none">
          <i class="fas fa-vial"></i>
          <span>TestOps Hub</span>
        </div>

        <!-- Google sign-up: complete profile step -->
        <div v-if="setupProfile || setupPendingApproval" class="setup-card setup-card--onboarding">
          <div v-if="setupPendingApproval" class="setup-pending">
            <div class="setup-pending-icon">
              <i class="fas fa-clock"></i>
            </div>
            <h2 class="login-title mb-2 text-center">Registration Successful</h2>
            <p class="setup-pending-message">
              Waiting for Admin Approval — Your account has been submitted for administrator review. You will be able to access the system after approval.
            </p>
            <button type="button" class="setup-btn-submit" @click="cancelSetup">
              Back to Login
            </button>
          </div>

          <template v-else>
            <div class="setup-onboard-header">
              <img
                v-if="setupProfile.picture"
                :src="setupProfile.picture"
                class="setup-avatar"
                referrerpolicy="no-referrer"
                alt=""
              />
              <div v-else class="setup-avatar setup-avatar--placeholder">
                <i class="fas fa-user"></i>
              </div>
              <div class="setup-onboard-header-text">
                <h2 class="setup-onboard-title">Complete your profile</h2>
                <p class="setup-onboard-subtitle">Sign in with Google — add your details to continue</p>
              </div>
            </div>

            <div class="setup-body setup-body--onboarding">
              <p class="setup-section-label">From Google</p>
              <div class="setup-grid-names">
                <div class="setup-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    class="setup-input setup-input-readonly"
                    :value="setupProfile.firstName"
                    readonly
                  />
                </div>
                <div class="setup-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    class="setup-input setup-input-readonly"
                    :value="setupProfile.lastName"
                    readonly
                  />
                </div>
              </div>
              <div class="setup-field">
                <label>Email</label>
                <input
                  type="email"
                  class="setup-input setup-input-readonly"
                  :value="setupProfile.email"
                  readonly
                />
              </div>

              <p class="setup-section-label setup-section-label--spaced">Your details</p>
              <div class="setup-field">
                <label>Nickname <span class="setup-required">*</span></label>
                <input
                  type="text"
                  v-model="setupForm.nickname"
                  class="setup-input setup-input--editable"
                  placeholder="e.g. NICKNAME"
                  autocomplete="nickname"
                />
              </div>
              <div class="setup-field setup-field--team">
                <label>Team <span class="setup-required">*</span></label>
                <div class="setup-team-list">
                  <label
                    v-for="opt in setupTeamOptions"
                    :key="opt.value"
                    class="setup-team-option"
                  >
                    <input
                      type="checkbox"
                      class="setup-team-checkbox"
                      :checked="setupForm.teams.includes(opt.value)"
                      @change="toggleSetupTeam(opt.value)"
                    />
                    <span>{{ opt.label }}</span>
                  </label>
                </div>
              </div>

              <div v-if="setupError" class="login-error setup-onboard-error">
                <i class="fas fa-exclamation-circle"></i> {{ setupError }}
              </div>
            </div>

            <div class="setup-footer setup-footer--onboarding">
              <button type="button" class="setup-btn-cancel" @click="cancelSetup">
                Back
              </button>
              <button
                type="button"
                class="setup-btn-submit setup-btn-submit--grow"
                :disabled="setupLoading"
                @click="completeProfile"
              >
                <span v-if="setupLoading"><i class="fas fa-spinner fa-spin me-1"></i> Saving...</span>
                <span v-else>Get started</span>
              </button>
            </div>
          </template>
        </div>

        <!-- Sign in -->
        <template v-else>
          <h2 class="login-title">Welcome back</h2>
          <p class="login-subtitle">Sign in with Google to continue</p>

          <div v-if="error" class="login-error">
            <i class="fas fa-exclamation-circle"></i>
            {{ error }}
          </div>
          <div v-if="showApiDebug" class="login-debug">
            <div><strong>Debug API URL</strong></div>
            <div><code>Resolved API base (from env):</code> {{ debugApiRaw }}</div>
            <div><code>API_BASE_URL:</code> {{ debugApiBase }}</div>
            <div><code>source:</code> {{ debugApiSource }}</div>
            <div><code>window.location.origin:</code> {{ browserOrigin }}</div>
            <div v-if="clusterDnsWarning" class="login-debug-warn">
              <strong>Config:</strong> {{ clusterDnsWarning }}
            </div>
          </div>

          <div class="google-btn-wrapper google-btn-wrapper--primary">
            <button
              v-if="useFirebaseGoogle"
              type="button"
              class="login-btn-google login-btn-google--primary"
              :disabled="googleLoading"
              @click="loginWithFirebaseGoogle"
            >
              <span v-if="googleLoading"><i class="fas fa-spinner fa-spin me-1"></i> Signing in...</span>
              <span v-else class="login-btn-google__content">
                <svg class="google-g-logo" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </span>
            </button>
            <div
              v-else
              class="google-signin-shell"
              :class="{ 'google-signin-shell--loading': googleLoading }"
            >
              <div class="google-signin-shell__face" aria-hidden="true">
                <span v-if="googleLoading">
                  <i class="fas fa-spinner fa-spin me-1"></i> Signing in...
                </span>
                <span v-else class="login-btn-google__content">
                  <svg class="google-g-logo" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </span>
              </div>
              <div id="google-signin-btn" class="google-signin-shell__hit"></div>
            </div>
          </div>

          <p class="login-footer-text">
            By signing in, you agree to use this tool for internal QA operations.
          </p>
        </template>
      </div>
    </div>

  </div>
</template>

<script>
import { mapMutations } from "vuex";
import {
  API_BASE_URL,
  API_BASE_SOURCE,
  RAW_VUE_APP_API_URL,
  clusterInternalApiUrlHelp,
  isClusterInternalApiUrl,
  fetchWithTimeout,
} from "@/config/apiBase";
import { signInWithGoogleAndGetIdToken } from "@/config/firebaseAuth";
import { resolveGoogleAuthMode, GOOGLE_SETUP_ERROR } from "@/config/googleAuthMode";
import { navigateAfterAuth } from "@/utils/menuAccess";
import { establishSessionAfterLogin } from "@/services/authSession";
import {
  serializeUserTeams,
  toggleTeamSelection,
} from "@/utils/userTeams";

export default {
  name: "SignIn",
  data() {
    return {
      error: null,
      form: { email: "", password: "" },
      showPassword: false,
      loginLoading: false,
      showForgot: false,
      forgotForm: { email: "", firstName: "", lastName: "", newPassword: "", confirmPassword: "" },
      forgotError: null,
      forgotLoading: false,
      forgotSuccess: false,
      showForcedPasswordChange: false,
      forcedEmail: "",
      forcedForm: { currentPassword: "", newPassword: "", confirmPassword: "" },
      forcedError: null,
      forcedLoading: false,
      setupProfile: null,
      setupPendingApproval: false,
      setupForm: { nickname: "", teams: [] },
      setupError: null,
      setupLoading: false,
      googleLoading: false,
      useFirebaseGoogle: false,
      googleClientId: "",
      debugApiRaw: RAW_VUE_APP_API_URL || "(empty at build time)",
      debugApiSource: API_BASE_SOURCE,
      browserOrigin: window.location.origin,
      showApiDebug: false,
    };
  },
  created() {
    this.setShowSidenav(false);
  },
  beforeUnmount() {
    this.setShowSidenav(true);
  },
  watch: {
    isSetupFlow(entering) {
      if (!entering && !this.useFirebaseGoogle) {
        this.scheduleGoogleButtonRender();
      }
    },
  },
  async mounted() {
    if (this.$route.query.reason === "session_expired") {
      this.error =
        "Session หมดอายุแล้ว (ทุก 24 ชั่วโมง) กรุณาเข้าสู่ระบบใหม่";
    }
    const mode = await resolveGoogleAuthMode();
    this.useFirebaseGoogle = mode.useFirebase;
    this.googleClientId = mode.googleClientId;
    if (!this.useFirebaseGoogle && !this.googleClientId && !this.error) {
      this.error = GOOGLE_SETUP_ERROR;
    }
    if (!this.useFirebaseGoogle) {
      this.loadGoogleScript();
    }
    const params = new URLSearchParams(window.location.search);
    this.showApiDebug = params.get("debugEnv") === "1";
  },
  computed: {
    debugApiBase() {
      if (API_BASE_URL) return API_BASE_URL;
      return typeof window !== "undefined"
        ? `${window.location.origin} (same-origin /api/*)`
        : "(same-origin /api)";
    },
    clusterDnsWarning() {
      return isClusterInternalApiUrl(API_BASE_URL) ? clusterInternalApiUrlHelp() : "";
    },
    isSetupFlow() {
      return !!(this.setupProfile || this.setupPendingApproval);
    },
    setupTeamOptions() {
      return [
        { value: "a", label: "Team A" },
        { value: "b", label: "Team B" },
        { value: "c", label: "Team C" },
      ];
    },
  },
  methods: {
    ...mapMutations(["setShowSidenav", "setUser", "setAuthenticatedUser"]),

    async completeLogin(user, token) {
      await establishSessionAfterLogin(user, token);
      this.setAuthenticatedUser(user);
      await this.goHomeAfterAuth();
    },

    async goHomeAfterAuth() {
      const target = await navigateAfterAuth(this.$store, this.$router);
      this.$router.push(target);
    },

    async parseApiResponse(res, fallbackMessage) {
      const contentType = res.headers.get("content-type") || "";
      const isJson = contentType.toLowerCase().includes("application/json");
      const body = isJson ? await res.json() : await res.text();
      if (!res.ok) {
        const bodyError =
          body && typeof body === "object"
            ? body.error || body.message
            : typeof body === "string" && body.trim().startsWith("<")
            ? `HTTP ${res.status} ${res.statusText} (upstream returned HTML)`
            : typeof body === "string"
            ? body
            : "";
        throw new Error(bodyError || fallbackMessage);
      }
      return body && typeof body === "object" ? body : {};
    },

    closeForgot() {
      this.showForgot = false;
      this.forgotForm = { email: "", firstName: "", lastName: "", newPassword: "", confirmPassword: "" };
      this.forgotError = null;
      this.forgotSuccess = false;
    },

    async resetPassword() {
      this.forgotError = null;
      const { email, firstName, lastName, newPassword, confirmPassword } = this.forgotForm;
      if (!email || !firstName || !lastName || !newPassword || !confirmPassword) {
        this.forgotError = "Please fill in all fields.";
        return;
      }
      if (newPassword !== confirmPassword) {
        this.forgotError = "Passwords do not match.";
        return;
      }
      if (newPassword.length < 6) {
        this.forgotError = "Password must be at least 6 characters.";
        return;
      }
      this.forgotLoading = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, firstName, lastName, newPassword }),
        });
        await this.parseApiResponse(res, "Failed to reset password");
        this.forgotSuccess = true;
      } catch (err) {
        this.forgotError = err.message;
      } finally {
        this.forgotLoading = false;
      }
    },

    clearLoginError() {
      this.error = null;
    },

    async loginWithPassword() {
      this.error = null;
      if (!this.form.email || !this.form.password) {
        this.error = "Please enter your email and password.";
        return;
      }
      if (isClusterInternalApiUrl(API_BASE_URL)) {
        this.error = clusterInternalApiUrlHelp();
        this.showApiDebug = true;
        return;
      }
      this.loginLoading = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: this.form.email, password: this.form.password }),
        });
        const data = await this.parseApiResponse(res, "Login failed");
        if (data.pendingApproval) {
          this.setupProfile = null;
          this.setupPendingApproval = true;
          return;
        }
        if (data.mustChangePassword) {
          this.forcedEmail = data.user?.email || this.form.email;
          this.forcedForm = {
            currentPassword: this.form.password,
            newPassword: "",
            confirmPassword: "",
          };
          this.forcedError = null;
          this.showForcedPasswordChange = true;
          return;
        }
        await this.completeLogin(data.user, data.token);
      } catch (err) {
        this.error = err.message || "Login failed";
        if (this.error && this.error.includes("Failed to fetch")) this.showApiDebug = true;
      } finally {
        this.loginLoading = false;
      }
    },

    closeForcedPassword() {
      this.showForcedPasswordChange = false;
      this.forcedEmail = "";
      this.forcedForm = { currentPassword: "", newPassword: "", confirmPassword: "" };
      this.forcedError = null;
    },

    async submitForcedPasswordChange() {
      this.forcedError = null;
      const { currentPassword, newPassword, confirmPassword } = this.forcedForm;
      if (!currentPassword || !newPassword || !confirmPassword) {
        this.forcedError = "Please fill in all password fields.";
        return;
      }
      if (newPassword !== confirmPassword) {
        this.forcedError = "New passwords do not match.";
        return;
      }
      if (newPassword.length < 6) {
        this.forcedError = "New password must be at least 6 characters.";
        return;
      }
      this.forcedLoading = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/change-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: this.forcedEmail,
            currentPassword,
            newPassword,
          }),
        });
        const data = await this.parseApiResponse(res, "Failed to update password");
        await this.completeLogin(data.user, data.token);
        this.closeForcedPassword();
        this.form.password = "";
      } catch (err) {
        this.forcedError = err.message;
      } finally {
        this.forcedLoading = false;
      }
    },

    loadGoogleScript() {
      if (!this.googleClientId) {
        this.error = GOOGLE_SETUP_ERROR;
        return;
      }
      if (window.google?.accounts?.id) {
        this.scheduleGoogleButtonRender();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => this.scheduleGoogleButtonRender();
      document.head.appendChild(script);
    },

    /** Full-width GIS button (invisible); visible face matches original styling without suggested email. */
    scheduleGoogleButtonRender() {
      if (this.useFirebaseGoogle || !this.googleClientId) return;
      this.$nextTick(() => {
        const el = document.getElementById("google-signin-btn");
        if (!el) return;
        if (!window.google?.accounts?.id) {
          this.loadGoogleScript();
          return;
        }
        el.innerHTML = "";
        const width = Math.max(280, Math.round(el.parentElement?.offsetWidth || 320));
        window.google.accounts.id.initialize({
          client_id: this.googleClientId,
          callback: this.handleGoogleCredential,
          auto_select: false,
        });
        window.google.accounts.id.renderButton(el, {
          type: "standard",
          theme: "outline",
          size: "large",
          width,
          text: "signin_with",
          shape: "rectangular",
          logo_alignment: "left",
        });
      });
    },

    async loginWithFirebaseGoogle() {
      this.error = null;
      if (isClusterInternalApiUrl(API_BASE_URL)) {
        this.error = clusterInternalApiUrlHelp();
        this.showApiDebug = true;
        return;
      }
      this.googleLoading = true;
      try {
        const idToken = await signInWithGoogleAndGetIdToken();
        await this.handleGoogleCredential({ credential: idToken });
      } catch (err) {
        this.error = err.message || "Google sign-in failed";
        if (this.error && this.error.includes("Failed to fetch")) this.showApiDebug = true;
      } finally {
        this.googleLoading = false;
      }
    },

    async handleGoogleCredential({ credential }) {
      this.error = null;
      this.googleLoading = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential }),
        });
        const data = await this.parseApiResponse(res, "Login failed");

        if (data.pendingApproval) {
          this.setupProfile = null;
          this.setupPendingApproval = true;
          this.setupError = null;
          return;
        }

        if (data.needsSetup) {
          this.setupPendingApproval = false;
          this.setupProfile = data.profile;
          this.setupForm = { nickname: "", teams: [] };
          this.setupError = null;
          return;
        }

        if (data.mustChangePassword) {
          this.forcedEmail = data.user?.email || "";
          this.forcedForm = { currentPassword: "", newPassword: "", confirmPassword: "" };
          this.forcedError = null;
          this.showForcedPasswordChange = true;
          return;
        }

        await this.completeLogin(data.user, data.token);
      } catch (err) {
        this.error = err.message;
      } finally {
        this.googleLoading = false;
      }
    },

    cancelSetup() {
      this.setupProfile = null;
      this.setupPendingApproval = false;
      this.setupForm = { nickname: "", teams: [] };
      this.setupError = null;
    },

    toggleSetupTeam(value) {
      this.setupForm.teams = toggleTeamSelection(this.setupForm.teams, value);
    },

    async completeProfile() {
      this.setupError = null;
      const nickname = this.setupForm.nickname.trim();
      const { teams } = this.setupForm;
      const team = serializeUserTeams(teams);
      const firstName = String(this.setupProfile?.firstName || "").trim();
      const lastName = String(this.setupProfile?.lastName || "").trim();
      if (!firstName || !lastName) {
        this.setupError = "Could not read your name from Google. Please try signing in again.";
        return;
      }
      if (!nickname) {
        this.setupError = "Please enter your nickname.";
        return;
      }
      if (!teams.length) {
        this.setupError = "Please select at least one team.";
        return;
      }
      this.setupLoading = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/complete-profile`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            googleId: this.setupProfile.googleId,
            email: this.setupProfile.email,
            firstName,
            lastName,
            nickname,
            team,
            teams,
            picture: this.setupProfile.picture,
          }),
        });
        const data = await this.parseApiResponse(res, "Failed to save profile");
        if (data.pendingApproval) {
          this.setupProfile = null;
          this.setupPendingApproval = true;
          return;
        }
        if (data.user) {
          await this.completeLogin(data.user, data.token);
        }
      } catch (err) {
        this.setupError = err.message;
      } finally {
        this.setupLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-layout {
  display: flex;
  min-height: 100vh;
  background: var(--surface-ground);
}

/* ── Left branding panel (default: wide — same as before layout change) ── */
.login-left {
  flex: 1;
  background: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  position: relative;
  overflow: hidden;
}

.login-left::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #344767 0%, #1a2942 100%);
}

.login-brand {
  position: relative;
  z-index: 1;
  color: #fff;
  max-width: 380px;
}

.login-logo {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: #fff;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(4px);
}

.login-app-name {
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;
}

.login-tagline {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 2.5rem;
}

.login-features {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.login-feature-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
}

.login-feature-item i {
  color: #66bb6a;
  font-size: 0.9rem;
  flex-shrink: 0;
}

/* ── Right form panel (default: narrow — same as before layout change) ── */
.login-right {
  flex: 0 0 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--surface-ground);
}

/* Complete profile / pending approval: wider form, narrower branding */
.login-layout--setup .login-left {
  flex: 0 0 380px;
  max-width: 38%;
}

.login-layout--setup .login-right {
  flex: 1;
  min-width: 0;
  padding: 2.5rem 3.5rem;
}

.login-card,
.setup-card {
  width: 100%;
  max-width: 360px;
  background: var(--surface-card);
  border-radius: var(--radius-card);
  border: 1px solid var(--surface-border);
  box-shadow: var(--shadow-card);
  padding: 2.5rem 2rem;
}

.setup-card {
  max-width: 520px;
  padding: 0;
  overflow: hidden;
}

/* Complete profile: onboarding card */
.login-layout--setup .login-right > .login-card {
  max-width: min(840px, 100%);
  width: 100%;
  padding: 0;
  border: none;
  box-shadow: none;
  background: transparent;
}

.login-layout--setup .setup-card--onboarding {
  max-width: none;
  width: 100%;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 8px 30px rgba(52, 71, 103, 0.1), 0 2px 8px rgba(52, 71, 103, 0.06);
  /* Allow BaseSelect menu to extend outside the card (same as Management filters). */
  overflow: visible;
}

.setup-onboard-header {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 2rem 3rem;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e9ecef;
}

.setup-onboard-header-text {
  min-width: 0;
}

.setup-onboard-title {
  font-size: 1.625rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.35rem;
  letter-spacing: -0.02em;
}

.setup-onboard-subtitle {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
}

.login-layout--setup .setup-card--onboarding .setup-avatar {
  width: 4.25rem;
  height: 4.25rem;
  border: 3px solid #fff;
  box-shadow: 0 4px 14px rgba(52, 71, 103, 0.15);
}

.setup-avatar--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e9ecef;
  color: #64748b;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.setup-body--onboarding {
  padding: 1.75rem 3rem 2rem;
  gap: 1.25rem;
  overflow: visible;
}

.setup-section-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  margin: 0 0 0.15rem;
}

.setup-section-label--spaced {
  margin-top: 0.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #f1f5f9;
}

.setup-grid-names {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}

.login-layout--setup .setup-card--onboarding .setup-field label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.35rem;
}

.setup-required {
  color: #e53e3e;
}

.login-layout--setup .setup-card--onboarding .setup-input {
  padding: 0.9rem 1.15rem;
  font-size: 1.0625rem;
  line-height: 1.45;
  min-height: 3.5rem;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.login-layout--setup .setup-card--onboarding .setup-input-readonly {
  background: #f1f5f9;
  border-color: #e2e8f0;
  color: #334155;
}

.login-layout--setup .setup-card--onboarding .setup-input--editable:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(52, 71, 103, 0.14);
  outline: none;
}

/* Team dropdown — same BaseSelect as Management / filters */
.setup-field--team {
  overflow: visible;
}

.setup-team-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid #d2d6da;
  border-radius: 0.5rem;
  background: #fff;
}

.setup-team-option {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0;
  font-size: 0.875rem;
  color: #344767;
  cursor: pointer;
}

.setup-team-checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: #344767;
}

.setup-field--team :deep(.base-select) {
  width: 100%;
  position: relative;
  z-index: 20;
}

.setup-field--team :deep(.base-select--open) {
  z-index: 1100;
}

.setup-field--team :deep(.base-select__menu) {
  z-index: 1100;
}

.login-layout--setup .setup-card--onboarding .setup-field--team :deep(.base-select--lg .base-select__trigger) {
  min-height: 3.5rem;
  padding: 0.9rem 1.15rem;
  font-size: 1.0625rem;
  border-radius: 10px;
  border-color: #cbd5e1;
}

.login-layout--setup .setup-card--onboarding .setup-field--team :deep(.base-select--open .base-select__trigger),
.login-layout--setup .setup-card--onboarding .setup-field--team :deep(.base-select__trigger:focus-visible) {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(52, 71, 103, 0.14);
}

.setup-footer--onboarding {
  display: flex;
  gap: 0.65rem;
  padding: 1rem 3rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e9ecef;
}

.login-layout--setup .setup-card--onboarding .setup-btn-cancel {
  flex: 0 0 auto;
  min-width: 5.5rem;
  padding: 0.5rem 1.1rem;
  font-size: 0.875rem;
  min-height: auto;
  border-radius: 8px;
}

.login-layout--setup .setup-card--onboarding .setup-btn-submit {
  flex: 1;
  padding: 0.55rem 1.15rem;
  font-size: 0.875rem;
  font-weight: 600;
  min-height: auto;
  border-radius: 8px;
}

.setup-onboard-error {
  margin-top: 0.25rem;
  margin-bottom: 0;
}

.login-layout--setup .setup-card--onboarding .setup-pending {
  padding: 3.5rem 3rem;
}

.login-layout--setup .setup-card--onboarding .setup-pending .setup-btn-submit {
  flex: none;
  width: auto;
  max-width: 16rem;
  margin: 0 auto;
  padding: 0.55rem 1.25rem;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .login-layout--setup .login-right {
    padding: 1.5rem;
  }

  .setup-onboard-header,
  .setup-body--onboarding,
  .setup-footer--onboarding {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .setup-grid-names {
    grid-template-columns: 1fr;
  }
}

.setup-pending {
  padding: 2.5rem 2rem;
  text-align: center;
}

.setup-pending-icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.25rem;
  border-radius: 50%;
  background: #fff8e6;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}

.setup-pending-message {
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.75rem;
}

.setup-card .setup-modal-header--flat {
  padding: 1.75rem 1.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
}

.setup-card .setup-body {
  padding: 1.25rem 1.75rem;
}

.setup-card .setup-footer--actions {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.75rem 1.75rem;
}

.setup-btn-submit--grow {
  flex: 1;
}

.setup-input-readonly {
  background: #f8f9fa;
  color: var(--text-secondary);
  cursor: default;
}

/* ── Mobile logo ── */
.login-mobile-logo {
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary);
}

.login-mobile-logo i {
  font-size: 1.4rem;
  color: var(--primary);
}

/* ── Card content ── */
.login-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.35rem;
}

.login-subtitle {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
}

/* ── Error ── */
.login-error {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  color: #c53030;
  border-radius: 8px;
  padding: 0.65rem 0.9rem;
  font-size: 0.8rem;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.login-debug {
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  color: #495057;
  border-radius: 8px;
  padding: 0.65rem 0.8rem;
  font-size: 0.75rem;
  margin-bottom: 1rem;
  line-height: 1.45;
}

.login-debug code {
  color: #344767;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.login-debug-warn {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #fed7d7;
  color: #c53030;
  font-size: 0.72rem;
  line-height: 1.4;
}

/* ── Email/Password form ── */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-bottom: 1.25rem;
}

.login-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.login-field label {
  font-size: 0.775rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.login-input-wrap {
  position: relative;
}

.login-input {
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.825rem;
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.15s;
  background: #fff;
}

.login-input:focus {
  border-color: var(--primary);
}

.login-input-wrap .login-input {
  padding-right: 2.5rem;
}

.login-eye {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  padding: 0;
  font-size: 0.8rem;
}

.login-eye:hover {
  color: var(--text-secondary);
}

.login-btn-submit {
  width: 100%;
  padding: 0.6rem;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  margin-top: 0.25rem;
}

.login-btn-submit:hover:not(:disabled) {
  opacity: 0.88;
}

.login-btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Divider ── */
.login-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: #adb5bd;
  font-size: 0.75rem;
}

.login-divider::before,
.login-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e9ecef;
}

/* ── Google button wrapper ── */
.google-btn-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.google-btn-wrapper--primary {
  margin-bottom: 1.25rem;
}

.login-btn-google {
  width: 100%;
  max-width: 320px;
  padding: 0.55rem 1rem;
  background: #fff;
  color: #3c4043;
  border: 1px solid #dadce0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, box-shadow 0.15s;
}

.login-btn-google:hover:not(:disabled) {
  background: #f8f9fa;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.12);
}

.login-btn-google:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.login-btn-google--primary {
  width: 100%;
  max-width: none;
}

.login-btn-google__content {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.google-g-logo {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: block;
}

/* GIS: pretty static face + invisible official button for clicks */
.google-signin-shell {
  position: relative;
  width: 100%;
  min-height: 44px;
  border: 1px solid #dadce0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  transition: background 0.15s, box-shadow 0.15s;
}

.google-signin-shell:hover:not(.google-signin-shell--loading) {
  background: #f8f9fa;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.12);
}

.google-signin-shell--loading {
  opacity: 0.65;
  pointer-events: none;
}

.google-signin-shell__face {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #3c4043;
  pointer-events: none;
  user-select: none;
}

.google-signin-shell__hit {
  position: absolute;
  inset: 0;
  z-index: 2;
  opacity: 0.01;
  overflow: hidden;
}

.google-signin-shell__hit :deep(> div) {
  width: 100% !important;
  height: 100% !important;
  min-height: 44px;
}

.google-signin-shell__hit :deep(iframe) {
  width: 100% !important;
  height: 44px !important;
  margin: 0 !important;
}

.login-btn-submit--secondary {
  background: #fff;
  color: var(--primary);
  border: 1px solid var(--primary);
}

.login-btn-submit--secondary:hover:not(:disabled) {
  background: var(--primary-light);
  opacity: 1;
}

/* ── Footer text ── */
.login-footer-text {
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-align: center;
  line-height: 1.5;
  margin: 0;
}

/* ── Responsive: mobile ── */
@media (max-width: 991px) {
  .login-right {
    flex: 1;
  }
}

/* ── Complete Profile Modal ── */
.setup-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.setup-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  overflow: hidden;
}

.setup-modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e9ecef;
}

.setup-avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.setup-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.15rem;
}

.setup-subtitle {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin: 0;
}

.setup-body {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.setup-row {
  display: flex;
  gap: 0.75rem;
}

.setup-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.setup-field label {
  font-size: 0.775rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.setup-input {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.45rem 0.75rem;
  font-size: 0.825rem;
  color: var(--text-primary);
  outline: none;
  width: 100%;
  transition: border-color 0.15s;
  background: #fff;
}

.setup-input:focus {
  border-color: var(--primary);
}

.setup-footer {
  padding: 1rem 1.5rem 1.5rem;
}

.setup-btn-submit {
  width: 100%;
  padding: 0.6rem;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

.setup-btn-submit:hover:not(:disabled) {
  opacity: 0.88;
}

.setup-btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.setup-btn-cancel {
  flex: 0 0 auto;
  padding: 0.6rem 1.2rem;
  background: #f1f3f5;
  color: var(--text-secondary);
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.setup-btn-cancel:hover {
  background: #e9ecef;
}

.login-forgot-link {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.775rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.login-forgot-link:hover {
  color: var(--primary);
}

.forgot-success {
  background: #f0fff4;
  border: 1px solid #c6f6d5;
  color: #276749;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.825rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
